import {
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TippyDirective } from '@app/shared/directives/tippy.directive';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { AssetInstance } from '../../core/models/asset-instance.model';
import { Form } from '../../core/models/form.model';
import { AssetInstanceService } from '../../core/services/asset-instance.service';
import { BreadcrumbService } from '../../core/services/breadcrumb.service';
import { CheckPointService } from '../../core/services/checkPoint.service';
import { EntitlementsService } from '../../core/services/entitlements.service';
import { FormService } from '../../core/services/form.service';
import { LoggerService } from '../../core/services/logger.service';
import { ModalConfig, ModalService } from '../../core/services/modal.service';
import { PermissionService } from '../../core/services/permission.service';
import { TranslationService } from '../../core/services/translation.service';
import { ButtonComponent } from '../../shared/components/button/button.component';
import { CardContentComponent } from '../../shared/components/card/card-content.component';
import { CardHeaderComponent } from '../../shared/components/card/card-header.component';
import { CardTitleComponent } from '../../shared/components/card/card-title.component';
import { CardComponent } from '../../shared/components/card/card.component';
import { EntityListSelectorService } from '../../shared/components/entity-list-selector/entity-list-selector.service';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { ModalDialogComponent } from '../../shared/components/modal-dialog/modal-dialog.component';
import { SpinnerComponent } from '../../shared/components/spinner/spinner.component';
import { UpsellDialogComponent } from '../../shared/components/upsell-dialog/upsell-dialog.component';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';
import { getCheckPointFormFields } from './checkpoint-form.definition';

@Component({
  selector: 'app-checkpoint-form',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    CardComponent,
    CardHeaderComponent,
    CardTitleComponent,
    CardContentComponent,
    FormlyModule,
    IconComponent,
    SpinnerComponent,
    ButtonComponent,
    TranslatePipe,
    TippyDirective,
    ModalDialogComponent,
  ],
  templateUrl: './checkPoint-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./checkPoint-form.component.css'],
})
export class CheckPointFormComponent implements OnInit, OnDestroy {
  private checkPointService = inject(CheckPointService);
  private assetInstanceService = inject(AssetInstanceService);
  private formService = inject(FormService);
  private entitySelectorService = inject(EntityListSelectorService);
  private permissionService = inject(PermissionService);
  private entitlementsService = inject(EntitlementsService);
  private modalService = inject(ModalService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private translate = inject(TranslationService);

  form = new FormGroup({});
  model: any = {};
  fields = signal<FormlyFieldConfig[]>([]);

  isEditMode = signal(false);
  loading = signal(false);
  saving = signal(false);
  deleting = signal(false);
  error = signal<string | null>(null);
  canDelete = signal(false);
  showDeleteModal = signal(false);
  checkPointId: string | null = null;
  formValid = signal(false); // Track form validity to avoid ExpressionChangedAfterItHasBeenCheckedError

  // Computed disabled state for save button
  saveDisabled = computed(() => !this.formValid() || this.saving());

  // Selected forms for the checkpoint
  selectedForms = signal<Form[]>([]);

  // Instance management
  instances = signal<AssetInstance[]>([]);
  showAddCopies = signal(false);
  showDeleted = signal(false);
  addCopiesQuantity = signal(10);
  addCopiesStartSerial = signal(1);
  addCopiesPrefix = signal('');
  addingCopies = signal(false);
  editingInstanceId = signal<string | null>(null);
  editingSerialValue = signal('');
  serialValidationError = signal<string | null>(null);
  private breadcrumbService = inject(BreadcrumbService);
  private logger = inject(LoggerService).getLogger('checkpoints');

  // Return URL for navigation after save/cancel
  private returnUrl: string | null = null;

  constructor() {
    // Set edit mode synchronously before first change detection
    const id = this.route.snapshot.paramMap.get('id');
    if (id && id !== 'new') {
      this.checkPointId = id;
      this.isEditMode.set(true);
    }
  }

  async ngOnInit() {
    // Defer initialization to avoid ExpressionChangedAfterItHasBeenCheckedError
    await Promise.resolve();

    // Capture returnUrl parameter for navigation after save/cancel
    this.returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');

    // Load form definition from in-code definition (moved from database form_definitions table)
    this.fields.set(getCheckPointFormFields((key) => this.translate.translate(key)));

    this.canDelete.set(this.permissionService.has('asset.delete'));

    // Load asset if in edit mode
    if (this.isEditMode() && this.checkPointId) {
      await this.loadCheckPoint(this.checkPointId);
      await this.loadInstances();
    } else {
      // New checkpoint — set static breadcrumb
      this.breadcrumbService.setDynamicLabel('New Checkpoint');
      // Auto-select forms when creating a new checkpoint
      await this.autoSelectForms();
    }

    // Watch for form validity changes
    this.form.statusChanges.subscribe(() => {
      this.formValid.set(this.form.valid);
    });
    this.form.valueChanges.subscribe(() => {
      this.formValid.set(this.form.valid);
    });

    // Set initial form validity
    this.formValid.set(this.form.valid);
  }

  ngOnDestroy() {
    // Cleanup if needed
  }

  /**
   * Auto-select forms when creating a new checkpoint
   * If there's only one form, select it automatically
   * If there are multiple forms, open the selection dialog automatically
   */
  private async autoSelectForms() {
    try {
      const allForms = await this.formService.getAll();

      if (allForms.length === 1) {
        // Single form: auto-select it
        this.selectedForms.set(allForms);
        this.logger.info('Auto-selected single form:', allForms[0].name);
      } else if (allForms.length > 1) {
        // Multiple forms: open selection dialog
        this.logger.info('Multiple forms available, opening selection dialog');
        await this.selectForms();
      }
      // If no forms (length === 0), user will select manually later
    } catch (err) {
      this.logger.error('Failed to auto-select forms:', err);
    }
  }

  /**
   * Open form selector dialog
   */
  async selectForms() {
    try {
      const forms = await this.entitySelectorService.selectForms({
        mode: 'multi',
        preselected: this.selectedForms().map((f) => f.id!),
        title: this.translate.translate('checkpoint.form.selectForms'),
        confirmText: this.translate.translate('checkpoint.form.confirmSelectForms'),
      });

      if (forms && forms.length > 0) {
        this.selectedForms.set(forms);
      }
    } catch (err) {
      this.logger.error('Failed to select forms:', err);
    }
  }

  /**
   * Remove a form from the selected list
   */
  removeForm(formId: string) {
    const updated = this.selectedForms().filter((f) => f.id !== formId);
    this.selectedForms.set(updated);
  }

  async loadCheckPoint(id: string) {
    this.loading.set(true);
    this.error.set(null);

    try {
      const checkPoint = await this.checkPointService.getById(id);

      if (checkPoint) {
        this.breadcrumbService.setDynamicLabel(checkPoint.name);
        // Load assigned interaction type IDs
        const typeIds = await this.checkPointService.getForms(id);

        this.model = {
          name: checkPoint.name,
          description: checkPoint.description || '',
        };

        // Load selected forms
        if (typeIds.length > 0) {
          const forms = await Promise.all(typeIds.map((id) => this.formService.getById(id)));
          this.selectedForms.set(forms.filter((f) => f !== null) as Form[]);
        }
      } else {
        this.error.set('Asset not found');
        setTimeout(() => this.router.navigate(['/checkpoints']), 2000);
      }
    } catch (err) {
      this.logger.error('Failed to load asset:', err);
      this.error.set('Failed to load asset. Please try again.');
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Defense-in-depth re-check of the plan's asset hard cap, for direct
   * navigation to /checkpoints/new that bypasses the list page's own gate
   * (see CheckPointsComponent.onNewCheckpointClick()). Editing an existing
   * checkpoint is never gated -- only creating a new one.
   */
  private async checkAssetLimit(): Promise<boolean> {
    let entitlements: Awaited<ReturnType<typeof this.entitlementsService.getCurrent>>;
    try {
      entitlements = await this.entitlementsService.getCurrent();
    } catch (err) {
      this.logger.warn('Failed to load entitlements, allowing checkpoint creation:', err);
      return true; // fail open if entitlements haven't synced/loaded yet
    }
    if (!entitlements) {
      return true; // fail open if entitlements haven't synced yet
    }

    const currentCount = await this.checkPointService.count();
    if (!this.entitlementsService.limitReached(currentCount, entitlements.asset_limit)) {
      return true;
    }

    const closeSignal = signal<{ action: string } | null>(null);
    const config: ModalConfig = {
      component: UpsellDialogComponent,
      componentInputs: { limitType: 'asset', planName: entitlements.plan_name },
      title: this.translate.translate('upsellDialog.title'),
      size: 'small',
      closeSignal,
      actionButtons: [
        {
          id: 'cancel',
          label: this.translate.translate('upsellDialog.cancel'),
          variant: 'text',
          onClick: async () => closeSignal.set({ action: 'cancel' }),
        },
        {
          id: 'upgrade',
          label: this.translate.translate('upsellDialog.upgradePlan'),
          variant: 'primary',
          onClick: async () => closeSignal.set({ action: 'upgrade' }),
        },
      ],
    };

    const result = await this.modalService.openModal(config);
    if (result?.action === 'upgrade') {
      this.router.navigate(['/settings/billing']);
    }
    return false;
  }

  /**
   * Core save logic shared by onSave() and onSaveAndClose().
   * Returns the saved checkpoint ID.
   */
  private async doSave(): Promise<string> {
    const formIds = this.selectedForms().map((f) => f.id!);

    const checkPointData = {
      name: this.model.name,
      description: this.model.description,
      qr_code: this.model.qr_code,
    };

    let savedCheckPointId: string;

    if (this.isEditMode() && this.checkPointId) {
      await this.checkPointService.update(this.checkPointId, checkPointData);
      savedCheckPointId = this.checkPointId;
    } else {
      const checkPoint = await this.checkPointService.create(checkPointData);
      savedCheckPointId = checkPoint.id!;

      if (!checkPointData.qr_code && checkPoint.id) {
        const qrCode = this.checkPointService.generateQrCode(checkPoint.id);
        await this.checkPointService.update(checkPoint.id, { qr_code: qrCode });
      }
    }

    await this.checkPointService.assignForms(savedCheckPointId, formIds);

    return savedCheckPointId;
  }

  /**
   * Save the checkpoint and remain on the form.
   * If this is a new checkpoint, transitions the URL to the edit URL.
   */
  async onSave() {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    this.saving.set(true);
    this.error.set(null);

    if (!this.isEditMode() && !(await this.checkAssetLimit())) {
      this.saving.set(false);
      return;
    }

    try {
      const wasNew = !this.isEditMode();
      const savedId = await this.doSave();

      if (wasNew) {
        this.checkPointId = savedId;
        this.isEditMode.set(true);
        this.router.navigate(['/checkpoints', savedId], { replaceUrl: true });
      }
    } catch (err) {
      this.logger.error('Failed to save asset:', err);

      let errorMessage = 'Failed to save asset. Please try again.';
      if (err instanceof Error && err.message) {
        errorMessage = `Failed to save asset: ${err.message}`;
      }

      this.error.set(errorMessage);
    } finally {
      this.saving.set(false);
    }
  }

  /**
   * Save the checkpoint and navigate away (original save behaviour).
   */
  async onSaveAndClose() {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    this.saving.set(true);
    this.error.set(null);

    if (!this.isEditMode() && !(await this.checkAssetLimit())) {
      this.saving.set(false);
      return;
    }

    try {
      await this.doSave();

      const destination = this.returnUrl || '/checkpoints';
      this.router.navigate([destination]);
    } catch (err) {
      this.logger.error('Failed to save asset:', err);

      let errorMessage = 'Failed to save asset. Please try again.';
      if (err instanceof Error && err.message) {
        errorMessage = `Failed to save asset: ${err.message}`;
      }

      this.error.set(errorMessage);
    } finally {
      this.saving.set(false);
    }
  }

  /** Called by the form's ngSubmit event — delegates to onSaveAndClose(). */
  async onSubmit() {
    await this.onSaveAndClose();
  }

  onCancel() {
    // Navigate to returnUrl if provided, otherwise go to checkpoints list
    const destination = this.returnUrl || '/checkpoints';
    this.router.navigate([destination]);
  }

  /**
   * Show QR code for generic asset access (shows interaction type choice)
   */
  showCheckPointQrCode() {
    if (!this.checkPointId) return;
    this.router.navigate(['/checkpoints', this.checkPointId]);
  }

  /**
   * Show view for a specific interaction type
   */
  showFormQrCode(interactionType: Form) {
    if (!this.checkPointId) return;
    this.router.navigate(['/checkpoints', this.checkPointId, interactionType.id, 'view']);
  }

  /**
   * Navigate to the View screen for a specific interaction type
   */
  showFormView(interactionType: Form) {
    if (!this.checkPointId) return;
    this.router.navigate(['/checkpoints', this.checkPointId, interactionType.id, 'view']);
  }

  /**
   * Navigate to interaction history page
   */
  viewFormSubmission(interactionType: Form) {
    if (!this.checkPointId) return;
    this.router.navigate(['/submission', this.checkPointId, interactionType.id, 'history'], {
      queryParams: { returnUrl: `/checkpoints/${this.checkPointId}` },
    });
  }

  /**
   * View history for a form (alias for viewInteractionHistory)
   */
  viewFormHistory(form: Form) {
    this.viewFormSubmission(form);
  }

  /**
   * Navigate to edited form/interaction type definition
   */
  editForm(form: Form) {
    if (!this.checkPointId) return;
    this.router.navigate(['/forms', form.id], {
      queryParams: { returnUrl: `/checkpoints/${this.checkPointId}` },
    });
  }

  /**
   * Navigate to the view screen for a specific instance.
   * - If exactly 1 form is assigned: go directly to the instance-scoped view.
   * - If multiple forms are assigned: reuse ChooseFormToSubmit in view-mode so the
   *   user picks which form to view.
   * - If no forms are assigned: do nothing (button is disabled).
   */
  showInstanceView(instance: AssetInstance) {
    if (!this.checkPointId) return;
    const forms = this.selectedForms();
    if (forms.length === 0) return;
    if (forms.length === 1) {
      this.router.navigate(['/checkpoints', this.checkPointId, forms[0].id, instance.id, 'view']);
    } else {
      this.router.navigate(['/submission', this.checkPointId], {
        queryParams: { mode: 'view', instanceId: instance.id },
      });
    }
  }

  openDeleteModal(): void {
    this.showDeleteModal.set(true);
  }

  cancelDelete(): void {
    this.showDeleteModal.set(false);
  }

  // === Instance Management ===

  async loadInstances(): Promise<void> {
    if (!this.checkPointId) return;
    try {
      // Load all instances (including deleted) so the deleted toggle can switch views locally.
      const allResults = await this.assetInstanceService.getAllByCheckpointId(this.checkPointId);
      this.instances.set(allResults);
    } catch (err) {
      this.logger.error('Failed to load instances:', err);
      this.instances.set([]);
    }
  }

  displayedInstances = computed(() => {
    const all = this.instances();
    return this.showDeleted() ? all : all.filter((i) => !i.is_deleted);
  });

  activeInstanceCount = computed(() => {
    return this.instances().filter((i) => !i.is_deleted).length;
  });

  previewSerials = computed(() => {
    const start = this.addCopiesStartSerial();
    const count = this.addCopiesQuantity();
    const prefix = this.addCopiesPrefix();
    const serials: string[] = [];
    const max = Math.min(count, 20);
    for (let i = 0; i < max; i++) {
      serials.push(prefix ? `${prefix}${start + i}` : String(start + i));
    }
    if (count > 20) {
      serials.push('...');
    }
    return serials.join(', ');
  });

  async openAddCopies(): Promise<void> {
    if (!this.checkPointId) return;
    const nextSerial = await this.assetInstanceService.getNextSerialNumber(this.checkPointId);
    this.addCopiesStartSerial.set(nextSerial);
    this.addCopiesQuantity.set(10);
    this.addCopiesPrefix.set('');
    this.showAddCopies.set(true);
  }

  cancelAddCopies(): void {
    this.showAddCopies.set(false);
  }

  async confirmAddCopies(): Promise<void> {
    if (!this.checkPointId) return;
    this.addingCopies.set(true);
    try {
      await this.assetInstanceService.createBatch({
        asset_id: this.checkPointId,
        start_serial: this.addCopiesStartSerial(),
        count: this.addCopiesQuantity(),
        prefix: this.addCopiesPrefix() || undefined,
      });
      this.showAddCopies.set(false);
      await this.loadInstances();
    } catch (err) {
      this.logger.error('Failed to create instances:', err);
      this.error.set('Failed to create copies. Please try again.');
    } finally {
      this.addingCopies.set(false);
    }
  }

  startEditSerial(instance: AssetInstance): void {
    this.editingInstanceId.set(instance.id);
    this.editingSerialValue.set(instance.serial_number);
    this.serialValidationError.set(null);
  }

  cancelEditSerial(): void {
    this.editingInstanceId.set(null);
    this.editingSerialValue.set('');
    this.serialValidationError.set(null);
  }

  async saveEditSerial(instance: AssetInstance): Promise<void> {
    const newSerial = this.editingSerialValue().trim();
    if (!newSerial) return;
    if (newSerial === instance.serial_number) {
      this.cancelEditSerial();
      return;
    }

    if (!this.checkPointId) return;
    const isUnique = await this.assetInstanceService.validateSerialUnique(
      this.checkPointId,
      newSerial,
      instance.id,
    );
    if (!isUnique) {
      this.serialValidationError.set(
        this.translate.translate('checkpoint.instances.duplicateSerial'),
      );
      return;
    }

    await this.assetInstanceService.update(instance.id, { serial_number: newSerial });
    this.cancelEditSerial();
    await this.loadInstances();
  }

  async deleteInstance(instance: AssetInstance): Promise<void> {
    await this.assetInstanceService.softDelete(instance.id);
    await this.loadInstances();
  }

  async restoreInstance(instance: AssetInstance): Promise<void> {
    await this.assetInstanceService.restore(instance.id);
    await this.loadInstances();
  }

  toggleShowDeleted(): void {
    this.showDeleted.set(!this.showDeleted());
  }

  async confirmDelete(): Promise<void> {
    const id = this.checkPointId;
    if (!id) return;

    this.deleting.set(true);
    try {
      this.showDeleteModal.set(false);
      await this.checkPointService.delete(id);
      this.router.navigate(['/checkpoints']);
    } catch (err) {
      this.logger.error('Failed to delete checkpoint:', err);
      this.error.set('Failed to delete. Please try again.');
    } finally {
      this.deleting.set(false);
    }
  }
}
